import { compact, flattenDeep, sortBy } from "lodash";
import { v4 as uuidv4 } from "uuid";
import XRegExp from "xregexp";

import {
  type BreakdownGroupDescriptor,
  type LabelFilter,
  type MatchWith,
  type MatcherDescriptor,
  type MatcherOperator,
  type SegmentDescriptor,
  type SegmentLevel,
  type SegmentOperator,
} from "./types";

interface LabelRulesByGroupV3LabelRule {
  name: string;
  active: boolean;
  output: string;
  priority: number;
  createdAt: any;
  matcher: any;
  _metadata: any | null;
}

interface LabelRulesByGroupV3 {
  id: string;
  groupId: string;
  name: string;
  level: string;
  active: boolean;
  isUsingSimpleUI: boolean;
  createdAt: any;
  updatedAt: any;
  labelRule: LabelRulesByGroupV3LabelRule[];
}

export function createEmptyMatcher(): MatcherDescriptor {
  return {
    id: uuidv4(),
    matchWith: "name",
    operator: "contains",
    value: "",
    error: null,
  };
}

export function createEmptySegment(): SegmentDescriptor {
  return {
    id: uuidv4(),
    name: "",
    active: true,
    level: "all",
    operator: "and",
    matchers: [],
    filters: [],
    error: null,
  };
}

export function createEmptyBreakdownGroup(): BreakdownGroupDescriptor {
  return {
    nameBeforeEdit: "My new breakdown",
    name: "My new breakdown",
    segments: [createEmptySegment()],
    error: null,
  };
}

function validateMatchers(matchers: MatcherDescriptor[]) {
  return matchers.map((matcher) => {
    const values = matchers.filter((e) => e !== matcher).map((e) => e.value);

    const hasDuplicateValue =
      matcher.value !== "" && values.includes(matcher.value);

    return hasDuplicateValue
      ? { ...matcher, error: "Duplicate, criteria" }
      : { ...matcher, error: null };
  });
}

function validateSegments(segments: SegmentDescriptor[]) {
  return segments.map((segment) => {
    const matchers = validateMatchers(segment.matchers);

    const hasEmptyCriteria =
      segment.name !== "" &&
      segment.matchers.filter((e) => e.value !== "").length === 0;
    const hasEmptyFilters = segment.name !== "" && segment.filters.length === 0;

    let error: string | null = null;

    if (segment.name === "") {
      error = "Segment required";
    }

    if (hasEmptyCriteria && hasEmptyFilters) {
      error = "Criteria required";
    }

    return { ...segment, matchers, error };
  });
}

export function validateBreakdownGroup(
  group: BreakdownGroupDescriptor,
  labelNames: string[],
) {
  const segments = validateSegments(group.segments);
  const filters = flattenDeep(segments.map((e) => e.filters));

  let error: string | null = null;

  if (group.name === "") {
    error = "Name required";
  }

  if (labelNames.includes(group.name)) {
    error = "Duplicate, breakdown name";
  }

  if (filters.some((e: string) => e.includes(`${group.name}:`))) {
    error = "Using filter with the same name as breakdown is not allowed";
  }

  return { ...group, segments, error };
}

export function getNumberOfErrors(group: BreakdownGroupDescriptor) {
  let errorsCount = 0;

  if (group.error) {
    errorsCount += 1;
  }

  group.segments.forEach((segment) => {
    if (segment.error) {
      errorsCount += 1;
    }

    segment.matchers.forEach((matcher) => {
      if (matcher.error) {
        errorsCount += 1;
      }
    });
  });

  return errorsCount;
}

export function isSegmentValidForPreview(segment: SegmentDescriptor) {
  const validatedMatchers = validateMatchers(segment.matchers);

  const hasName = segment.name !== "";
  const hasMatchers = segment.matchers.filter((e) => e.value !== "").length > 0;
  const allMatchersValid = validatedMatchers.filter((e) => !e.error).length > 0;
  const hasFilters = segment.filters.length > 0;

  return hasName && ((hasMatchers && allMatchersValid) || hasFilters);
}

type LabelRuleLevel = Exclude<SegmentLevel, "all">;

interface LabelRuleSegmentMetadata {
  segmentId: string;
  priority: number;
  operator: SegmentOperator;
  matchers: {
    matchWith: MatchWith;
    operator: MatcherOperator;
    value: string;
  }[];
  filters: string[][];
  level: SegmentLevel;
}

interface LabelRuleSegment {
  name: string;
  output: string;
  active: boolean;
  priority: number;
  matcher: any;
  createdAt?: string;
  _metadata: LabelRuleSegmentMetadata;
}

interface LabelRule {
  name: string;
  level: LabelRuleLevel;
  labelRules: LabelRuleSegment[];
  isUsingSimpleUI: boolean;
}

export function isCustomSpendMatcher(matcher: string) {
  return (
    matcher === "custom-spend-api-platform" || matcher === "custom-spend-sheet"
  );
}

export function matcherAsJSON(matcher: MatcherDescriptor): any {
  let operator = matcher.operator;
  let value = matcher.value;
  let matchWith = matcher.matchWith as string;

  if (matcher.matchWith === "custom-spend-sheet") {
    matchWith = "name";
  }
  if (matcher.matchWith === "custom-spend-api-platform") {
    matchWith = "nbt.platform";
  }

  if (matcher.operator === "==" || matcher.operator === "!=") {
    operator = "~";
    value = `(?i)^${matcher.value}$`;
  } else if (
    matcher.operator === "contains" ||
    matcher.operator === "!contains"
  ) {
    operator = "~";
    value = `(?i)${XRegExp.escape(matcher.value)}`;
  }

  let jsonLogic: any = { [operator]: [{ var: matchWith }, value] };

  if (matcher.operator === "!=" || matcher.operator === "!contains") {
    jsonLogic = { "!": [jsonLogic] };
  }

  if (isCustomSpendMatcher(matcher.matchWith)) {
    const logic = {
      and: [
        {
          or: [
            {
              "==": [
                {
                  var: "adKey.kind",
                },
                "custom_spend",
              ],
            },
            {
              "==": [
                {
                  var: "adKey.kind",
                },
                "custom_spend_api",
              ],
            },
          ],
        },
        jsonLogic,
      ],
    };

    return logic;
  } else {
    return jsonLogic;
  }
}

export function segmentAsJSON(
  segment: SegmentDescriptor,
  labelFilters: LabelFilter[],
): LabelRuleSegment {
  const matchers = segment.matchers
    .filter((e) => e.value !== "")
    .map((matcher) => matcherAsJSON(matcher));

  let matcher: any = { "==": [1, 0] }; // matches nothing by default
  let criteriaMatcher: any = null;
  let filtersMatcher: any = null;

  if (matchers.length > 0) {
    criteriaMatcher = { [segment.operator]: matchers };
  }

  if (segment.filters.length > 0) {
    const filtersLogic = compact(
      segment.filters.map((group) => {
        const groupMatchers = compact(
          group.map((g) => {
            const labelFilter = labelFilters.find((e) => e.id === g);
            return labelFilter ? labelFilter.matcher : null;
          }),
        );

        if (groupMatchers.length === 0) {
          return null;
        } else if (groupMatchers.length === 1) {
          return groupMatchers[0];
        } else {
          return { and: groupMatchers };
        }
      }),
    );

    if (filtersLogic.length === 1) {
      filtersMatcher = filtersLogic[0];
    } else if (filtersLogic.length > 1) {
      filtersMatcher = { or: filtersLogic };
    }
  }

  if (criteriaMatcher && filtersMatcher) {
    matcher = { and: [criteriaMatcher, filtersMatcher] };
  } else if (criteriaMatcher) {
    matcher = criteriaMatcher;
  } else if (filtersMatcher) {
    matcher = filtersMatcher;
  }

  return {
    name: segment.name.trim(),
    output: segment.name.trim(),
    active: segment.active,
    priority: 1,
    matcher,
    createdAt: segment.createdtAt,
    _metadata: {
      segmentId: segment.id,
      priority: 0,
      operator: segment.operator,
      matchers: segment.matchers.map((e) => ({
        matchWith: e.matchWith,
        operator: e.operator,
        value: e.value,
      })),
      filters: segment.filters,
      level: segment.level,
    },
  };
}

export function breakdownGroupToLabelRules(
  breakdownGroup: BreakdownGroupDescriptor,
  labelFilters: LabelFilter[],
) {
  const byLevel: Record<LabelRuleLevel, LabelRule | undefined> = {
    campaign: undefined,
    adset: undefined,
    ad: undefined,
  };

  breakdownGroup.segments
    .filter((e) => e.name !== "")
    .forEach((segment, idx) => {
      const level: LabelRuleLevel =
        segment.level === "all" ? "campaign" : segment.level;

      const labelRules = segmentAsJSON(segment, labelFilters);
      labelRules._metadata.priority = idx;

      let labelRule = byLevel[level];

      if (!labelRule) {
        labelRule = {
          name: breakdownGroup.name.trim(),
          level,
          labelRules: [],
          isUsingSimpleUI: true,
        };
      }

      labelRule.labelRules = [
        ...labelRule.labelRules,
        { ...labelRules, priority: labelRule.labelRules.length + 1 },
      ];

      byLevel[level] = labelRule;
    });

  return byLevel;
}

interface IntermediateSegment extends SegmentDescriptor {
  priority: number;
}

export function labelRulesToBreakdownGroup(labelRules: LabelRulesByGroupV3[]) {
  const intermediateSegments: IntermediateSegment[] = [];

  labelRules.forEach((labelRule) => {
    const segmentsByPriority = sortBy(labelRule.labelRule, "priority");

    segmentsByPriority.forEach((segment) => {
      const metadata: LabelRuleSegmentMetadata = segment._metadata;

      const existing = intermediateSegments.find(
        (e) => e.id === metadata.segmentId,
      );

      if (existing) {
        existing.level = "all";
      } else {
        const matchers = (metadata.matchers || []).map((matcher) => ({
          id: uuidv4(),
          matchWith: matcher.matchWith,
          operator: matcher.operator,
          value: matcher.value,
          error: null,
        }));

        intermediateSegments.push({
          id: metadata.segmentId,
          name: segment.output.trim(),
          active: segment.active,
          level: metadata.level ?? (labelRule.level as SegmentLevel),
          priority: metadata.priority,
          operator: metadata.operator,
          matchers,
          filters: metadata.filters,
          createdtAt: segment.createdAt,
          error: null,
        });
      }
    });
  });

  const segments = sortBy(intermediateSegments, "priority").map((segment) => ({
    id: segment.id,
    name: segment.name,
    active: segment.active,
    level: segment.level,
    operator: segment.operator,
    matchers: segment.matchers,
    filters: segment.filters,
    createdtAt: segment.createdtAt,
    error: segment.error,
  }));

  return {
    nameBeforeEdit: labelRules[0].name,
    name: labelRules[0].name,
    segments,
    error: null,
  };
}
